"use client";

import React, { useState, useEffect } from 'react'
import { Link, usePathname } from '@/i18n/navigation'
import { FaSearch, FaCalculator, FaChartBar, FaDatabase, FaBars, FaTimes } from 'react-icons/fa'
import { useTranslations } from 'next-intl'
import Header from '@/components/header'
import Footer from '@/components/footer'

interface NavItem {
	title: string
	href: string
	icon: React.ReactNode
	subitems?: { title: string; href: string }[]
}

interface DocsLayoutProps {
	children: React.ReactNode
}

export default function DocsLayout({ children }: DocsLayoutProps) {
	const t = useTranslations('docs')
	const headerT = useTranslations('header')
	const pathname = usePathname()
	const [hash, setHash] = useState('')
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

	useEffect(() => {
		const updateHash = () => setHash(window.location.hash)
		updateHash()
		window.addEventListener('hashchange', updateHash)
		return () => window.removeEventListener('hashchange', updateHash)
	}, [])

	const isActive = (href: string) => {
		const normalizedPathname = pathname.replace(/\/$/, '')
		const normalizedHref = href.replace(/\/$/, '')
		return normalizedPathname === normalizedHref || normalizedPathname.startsWith(normalizedHref + '/')
	}

	const isSubItemActive = (href: string) => {
		const currentPath = pathname + hash
		return currentPath === href || (hash && currentPath.startsWith(href))
	}

	const navigation: NavItem[] = [
		{
			title: t('navigation.authorSearch.title'),
			href: '/docs/author-search',
			icon: <FaSearch className="h-4 w-4" />,
			subitems: [
				{ title: t('navigation.authorSearch.basic'), href: '/docs/author-search#basic' },
				{ title: t('navigation.authorSearch.advanced'), href: '/docs/author-search#advanced' },
				{ title: t('navigation.authorSearch.integration'), href: '/docs/author-search#integration' },
			]
		},
		{
			title: t('navigation.metrics.title'),
			href: '/docs/metrics',
			icon: <FaCalculator className="h-4 w-4" />,
			subitems: [
				{ title: t('navigation.metrics.hIndex'), href: '/docs/metrics#h-index' },
				{ title: t('navigation.metrics.qScore'), href: '/docs/metrics#q-score' },
				{ title: t('navigation.metrics.iScore'), href: '/docs/metrics#i-score' },
				{ title: t('navigation.metrics.citations'), href: '/docs/metrics#citations' },
			]
		},
		{
			title: t('navigation.analysis.title'),
			href: '/docs/analysis',
			icon: <FaChartBar className="h-4 w-4" />,
			subitems: [
				{ title: t('navigation.analysis.publications'), href: '/docs/analysis#publications' },
				{ title: t('navigation.analysis.trends'), href: '/docs/analysis#trends' },
				{ title: t('navigation.analysis.visualizations'), href: '/docs/analysis#visualizations' },
			]
		},
		{
			title: t('navigation.export.title'),
			href: '/docs/export',
			icon: <FaDatabase className="h-4 w-4" />,
			subitems: [
				{ title: t('navigation.export.excel'), href: '/docs/export#excel' },
				{ title: t('navigation.export.word'), href: '/docs/export#word' },
				{ title: t('navigation.export.csv'), href: '/docs/export#csv' },
			]
		},
	]

	const navColors = [
		{ bg: 'bg-blue-500', hover: 'hover:bg-blue-600', light: 'bg-blue-50', text: 'text-blue-700', ring: 'ring-blue-500', border: 'border-blue-500' },
		{ bg: 'bg-purple-500', hover: 'hover:bg-purple-600', light: 'bg-purple-50', text: 'text-purple-700', ring: 'ring-purple-500', border: 'border-purple-500' },
		{ bg: 'bg-green-500', hover: 'hover:bg-green-600', light: 'bg-green-50', text: 'text-green-700', ring: 'ring-green-500', border: 'border-green-500' },
		{ bg: 'bg-orange-500', hover: 'hover:bg-orange-600', light: 'bg-orange-50', text: 'text-orange-700', ring: 'ring-orange-500', border: 'border-orange-500' },
	]

	return (
		<div className="flex flex-col min-h-screen bg-gray-50 smooth-scroll">
			<Header name={t('main.title')} desc={headerT('description')} />
			
			<div className="flex-1 flex">
				{/* Mobile Menu Button */}
				<button
					onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
					className="lg:hidden fixed bottom-4 right-4 z-50 bg-bme-main text-white p-3 rounded-full shadow-lg hover:bg-bme-main-hover transition-colors"
					aria-label={t('mobileMenu.toggle')}
				>
					{isMobileMenuOpen ? <FaTimes /> : <FaBars />}
				</button>

				{/* Sidebar Navigation */}
				<div className={`
					fixed inset-y-0 left-0 transform lg:sticky lg:top-0 lg:self-start lg:h-screen lg:translate-x-0 lg:block w-56 border-r border-gray-200 bg-linear-to-b from-gray-50 to-white overflow-y-auto transition-transform duration-300 ease-in-out z-40
					${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
				`}>
					<nav className="p-4 space-y-1 flex flex-col items-center">
						{navigation.map((item, index) => {
							const color = navColors[index % navColors.length]
							const itemActive = isActive(item.href)
							
							return (
								<div key={item.href} className="w-full">
									<Link
										href={item.href}
										onClick={() => setIsMobileMenuOpen(false)}
										className={`
											group flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 w-full relative
											${itemActive 
												? `${color.bg} text-white shadow-lg scale-[1.02] ring-2 ring-offset-2 ring-offset-white ${color.ring}` 
												: `text-gray-700 ${color.hover} hover:text-white hover:shadow-sm hover:scale-[1.01]`}
										`}
									>
										<span className={`${itemActive ? 'text-white' : color.text} transition-colors`}>
											{item.icon}
										</span>
										<span className={`text-center ${itemActive ? 'font-semibold' : ''}`}>
											{item.title}
										</span>
										{itemActive && (
											<span className="absolute right-2 w-1.5 h-1.5 rounded-full bg-white opacity-80" />
										)}
									</Link>
									
									{item.subitems && itemActive && (
										<div className="mt-1 space-y-0.5 w-full flex flex-col items-center">
											{item.subitems.map((subitem) => {
												const subItemActive = isSubItemActive(subitem.href)
												return (
													<Link
														key={subitem.href}
														href={subitem.href}
														onClick={() => setIsMobileMenuOpen(false)}
														className={`
															block px-2 py-1.5 text-xs font-medium rounded-md transition-all duration-200 text-center w-full relative
															${subItemActive
																? `${color.light} ${color.text} font-semibold border-l-2 ${color.border}`
																: 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}
														`}
													>
														{subitem.title}
													</Link>
												)
											})}
										</div>
									)}
								</div>
							)
						})}
					</nav>
				</div>

				{/* Overlay for mobile */}
				{isMobileMenuOpen && (
					<div 
						className="fixed inset-0 bg-black/20 backdrop-blur-sm lg:hidden z-30"
						onClick={() => setIsMobileMenuOpen(false)}
					/>
				)}

				{/* Main Content */}
				<div className="flex-1 overflow-y-auto">
					<main className="max-w-4xl mx-auto px-4 py-8 pb-32">
						{children}
					</main>
				</div>
			</div>

			<Footer />
		</div>
	)
}

